import { Api } from './api';
import { CategoryData, CategoryType, PatientForm } from '../revamp/types/PatientFormTypes';

/**
 * Response from the API when getting patient details
 */
interface PatientBackgroundResponse {
  [category: string]: any;
}

type FormSpecifics = {
  appointmentId?: string;
  therapyId?: string;
}

/**
 * Response from the API when submitting a form
 */
interface FormSubmissionResponse {
  formId: string;
  patientId: string;
  type: string;
  submissionDate: string;
  categories: string[];
}

/**
 * Response from the API when updating a category
 */
interface CategoryUpdateResponse {
  patientId: string;
  category: CategoryType;
  data: CategoryData;
  updatedAt: string;
}

/**
 * Options for getting form history
 */
interface FormHistoryOptions {
  type?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  skip?: number;
  category?: string;
}

/**
 * Form history response
 */
interface FormHistoryResponse {
  forms: Array<{
    formId: string;
    patientId: string;
    type: string;
    submissionDate: string;
    categories: string[];
    formData: any;
  }>;
  total: number;
  hasMore: boolean;
}

interface FormData {
  _id: string;
  patientId: string;
  appointmentId?: string;
  therapyId?: string;
  type: string;
  progress: string;
  categories: string[];
  formData: Record<string, any>;
  createdBy: string;
  updatedBy: string;
}

/**
 * Patient Forms Service
 * 
 * This service provides methods for interacting with the patient forms API.
 * It handles both individual category updates and complete form submissions.
 */
class PatientFormsService {
  /**
   * Get the current patient details
   * 
   * @param patientId The patient ID
   * @param categories Optional array of category names to filter by
   * @returns The current patient details
   */
  async getPatientBackground(patientId: string, categories?: CategoryType[]) {
    const response = await Api.get(`/patient-forms/background/${patientId}`, {
      params: { categories }
    });
    return response.data;
  }

  /**
   * Get a specific form
   * 
   * @param formId The form ID
   * @returns The form data
   */
  async getPatientFormById(formId: string): Promise<PatientForm | null> {
    try {
      const response = await Api.get(`/patient-forms/${formId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting form:", error);
      return null;
    }
  }

  /**
   * Get form history for a patient
   * 
   * @param patientId The patient ID
   * @param options Query options
   * @returns Array of forms
   */
  async getFormHistory(
    patientId: string,
    options: FormHistoryOptions = {}
  ): Promise<FormHistoryResponse> {
    try {
      const response = await Api.get(`/patient-forms/history/${patientId}`, {
        params: options,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting form history:", error);
      throw error;
    }
  }

  /**
   * Get history for a specific category
   * 
   * @param patientId The patient ID
   * @param category The category name
   * @returns Array of category changes
   */
  async getCategoryHistory(patientId: string, category: string): Promise<any[]> {
    try {
      const response = await Api.get(
        `/patient-forms/category/${patientId}/${category}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting category history:", error);
      throw error;
    }
  }

  /**
   * Get a historical snapshot of patient details
   * 
   * @param patientId The patient ID
   * @param date Target date
   * @returns Historical patient details
   */
  async getHistoricalSnapshot(
    patientId: string,
    date: string
  ): Promise<PatientBackgroundResponse> {
    try {
      const response = await Api.get(
        `/patient-forms/snapshot/${patientId}`,
        {
          params: { date },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting historical snapshot:", error);
      throw error;
    }
  }

  
  async getPatientForm(
    patientId: string, 
    formType: string, 
    queryDetails: FormSpecifics
  ): Promise<PatientForm | null> {
    try {
      const response = await Api.get(`/patient-forms/patient/${patientId}`, {
        params: { type: formType, queryDetails }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async createForm(patientId: string, formType: string, progress: string, queryDetails: FormSpecifics): Promise<FormData> {
    const response = await Api.post("/patient-forms", {
      patientId,
      type: formType,
      progress,
      ...queryDetails
    });
    return response.data;
  }

  async updateFormProgress(formId: string, progress: string): Promise<void> {
    await Api.patch(`/patient-forms/${formId}/progress`, {
      progress,
    });
  }

  async updateCategory(patientId: string, category: CategoryType, data: CategoryData) {
    const response = await Api.put(`/patient-forms/category/${patientId}/${category}`, data);
    return response.data;
  }

  async submitForm(formType: string, patientId: string, formId: string, queryDetails: FormSpecifics) {
    const response = await Api.post(`/patient-forms/submit/${formId}`, {
      formType,
      patientId,
      ...queryDetails
    });
    return response.data;
  }
}

// Export a singleton instance
export const patientFormsService = new PatientFormsService();
